import { useEffect, useRef, useState, useCallback } from 'react';
import '../Accounting/Accounting.css';
import { useReactToPrint } from 'react-to-print';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const StudentReceiptModal = ({ visible, onClose, paymentData }) => {
  const { t, i18n } = useTranslation();
  const componentRef = useRef();

  const formatNumber = (number, locale) => {
    const parsedNumber = parseFloat(number);
    if (isNaN(parsedNumber)) {
      return '0.00';
    }
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parsedNumber);
  };

  const formatDate = (dateString, locale) => {
    const date = new Date(dateString);
    return moment(date).format('DD MMM YY');
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const parseInventoryItems = (items) => {
    try {
      return items.map(item => JSON.parse(item));
    } catch (error) {
      console.error('Error parsing inventory items:', error);
      return [];
    }
  };

  const calculateTotalAmount = useCallback(() => {
    const classPrice = parseFloat(paymentData?.price) || 0;
    const parsedInventoryItems = paymentData?.inventory_items
      ? parseInventoryItems(paymentData.inventory_items)
      : [];
    const inventoryTotal = parsedInventoryItems.reduce(
      (sum, item) => sum + parseFloat(item.price_per_unit || 0),
      0
    );
    return classPrice + inventoryTotal;
  }, [paymentData]);

  useEffect(() => {
  }, [visible, paymentData, calculateTotalAmount]);

  if (!visible) return null;

  const parsedInventoryItems = paymentData?.inventory_items
    ? parseInventoryItems(paymentData.inventory_items)
    : [];

  // Generate a receipt number
  const receiptNo = `RE-${moment(paymentData?.last_payment_date || new Date()).format('YYYYMMDDHHmm')}`;

  // Calculate values
  const classPrice = parseFloat(paymentData?.price) || 0;
  const inventoryTotal = parsedInventoryItems.reduce(
    (sum, item) => sum + parseFloat(item.price_per_unit || 0),
    0
  );
  const totalBeforeDiscount = classPrice + inventoryTotal;
  const discountAmount = parseFloat(paymentData?.discount) || 0;
  const totalAfterDiscount = totalBeforeDiscount - discountAmount;
  const vatAmount = parseFloat(paymentData?.vat) || 0;
  const grandTotal = parseFloat(paymentData?.final_price) || (totalAfterDiscount + vatAmount);
  const payAmount = parseFloat(paymentData?.pay_amount) || 0;
  const remainingBalance = parseFloat(paymentData?.remaining_balance) || 0;

  return (
    <div className="invoice-modal-overlay" style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div className="invoice-modal" ref={componentRef} style={{ 
        backgroundColor: 'white',
        padding: '20px',
        width: '210mm',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative',
        fontFamily: '"Sarabun", Arial, sans-serif'
      }}>
        {/* Refund watermark if applicable */}
        {paymentData?.is_refunded && (
          <div className="refund-watermark" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            fontSize: '72px',
            color: 'rgba(255, 0, 0, 0.2)',
            fontWeight: 'bold',
            zIndex: 0,
            pointerEvents: 'none'
          }}>
            {t('refund')}
          </div>
        )}

        <span className="modal-close-button" onClick={onClose} style={{
          position: 'absolute',
          top: '10px',
          right: '15px',
          fontSize: '24px',
          cursor: 'pointer',
          zIndex: 1
        }}>&times;</span>

        {/* Header section with logo and company info */}
        <div className="modal-header" style={{ 
          display: 'flex', 
          marginBottom: '20px',
          alignItems: 'flex-start'
        }}>
          {/* Left side - Logo */}
          <div className="company-logo" style={{ 
            width: '80px', 
            height: '80px',
            marginRight: '20px'
          }}>
            <img src="/images/plann_logo.png" alt="logo" style={{ 
              width: '130%',
              height: 'auto'
            }} />
          </div>
          
          {/* Middle - Company info */}
          <div style={{ flex: 1, paddingTop: '5px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 'bold', 
              marginBottom: '5px',
              marginTop: 0,
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>PLANN TECH Co.,Ltd.</h3>
            <p style={{ 
              fontSize: '13px', 
              margin: '2px 0',
              lineHeight: '1.3',
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>123 Sukhumvit, Khlong Toey</p>
            <p style={{ 
              fontSize: '13px', 
              margin: '2px 0',
              lineHeight: '1.3',
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>Khlong Toey, Bangkok 10110</p>
            <p style={{ 
              fontSize: '13px', 
              margin: '2px 0',
              lineHeight: '1.3',
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>TAX ID 0105565096587</p>
            <p style={{ 
              fontSize: '13px', 
              margin: '2px 0',
              lineHeight: '1.3',
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>Tel: 062 2644 595</p>
            <p style={{ 
              fontSize: '13px', 
              margin: '2px 0',
              lineHeight: '1.3',
              color: '#000000',
              textAlign: 'left',
              marginLeft: '30px'
            }}>Email: info@plann.tech</p>
          </div>
          
          {/* Right side - Receipt box */}
          <div className="invoice-box" style={{ 
            backgroundColor: 'rgb(240, 245, 255)', 
            padding: '15px', 
            color: '#000',
            borderRadius: '5px',
            width: '200px'
          }}>
            <h2 style={{ 
              color: 'rgb(70, 70, 255)', 
              fontSize: '24px', 
              margin: '0 0 15px 0',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              {t('receipt').toUpperCase()}
            </h2>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              fontSize: '13px', 
              margin: '5px 0' 
            }}>
              <span style={{ fontWeight: 'normal' }}>{t('No.')}:</span>
              <span>{receiptNo}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              fontSize: '13px', 
              margin: '5px 0' 
            }}>
              <span style={{ fontWeight: 'normal' }}>{t('date')}:</span>
              <span>{formatDate(paymentData?.last_payment_date || paymentData?.last_payment_date, i18n.language)}</span>
            </div>
          </div>
        </div>

        <hr style={{ 
          border: 'none', 
          height: '1px', 
          backgroundColor: '#ddd', 
          margin: '0 0 15px 0' 
        }} />

        {/* Customer Information */}
        <div className="customer-info" style={{ marginBottom: '15px' }}>
          <h3 style={{ 
            fontSize: '14px', 
            fontWeight: 'bold', 
            marginBottom: '20px',
            color: '#000000',
            paddingTop: '15px'
          }}>{t('Customer Information')}</h3>
          <p style={{ fontSize: '13px', margin: '10px 0', color: '#000000' }}>
            <strong>{t('name')}:</strong> {paymentData?.firstname} {paymentData?.lastname || ''} 
          </p>
          <p style={{ fontSize: '13px', margin: '10px 0', color: '#000000' }}>
            <strong>{t('address')}:</strong> {paymentData?.address || t('na')} 
          </p>
          <p style={{ fontSize: '13px', margin: '10px 0', color: '#000000' }}>
             <strong>{t('phone')}:</strong> {paymentData?.phone || t('na')}
          </p>           
        </div>

        <hr style={{ 
          border: 'none', 
          height: '1px', 
          backgroundColor: 'rgb(220, 220, 240)', 
          margin: '15px 0'
        }} />

        {/* Receipt Table */}
        <div className="invoice-table" style={{ marginTop: '20px' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            fontSize: '12px'
          }}>
            <thead>
              <tr style={{ 
                backgroundColor: 'rgb(240, 240, 250)', 
                color: 'rgb(50, 50, 50)'
              }}>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '5%', textAlign: 'center' }}>#</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '45%' }}>{t('description')}</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '10%', textAlign: 'center' }}>{t('quantity')}</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '10%', textAlign: 'center' }}>{t('unit')}</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '15%', textAlign: 'right' }}>{t('unitPrice')}</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', width: '15%', textAlign: 'right' }}>{t('total')}</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ backgroundColor: 'rgb(248, 248, 255)' }}>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>1</td>
                <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                  {paymentData?.class_code}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>1</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Course</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                  {formatNumber(classPrice, i18n.language)}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                  {formatNumber(classPrice, i18n.language)}
                </td>
              </tr>
              
              {/* Inventory items */}
              {parsedInventoryItems.map((item, index) => (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : 'rgb(248, 248, 255)' }}>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{index + 2}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.label}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>1</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Book</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                    {formatNumber(item.price_per_unit, i18n.language)}
                  </td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                    {formatNumber(item.price_per_unit, i18n.language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Summary Information */}
        <div className="invoice-summary" style={{ 
          marginLeft: 'auto', 
          width: '250px', 
          marginTop: '20px',
          fontSize: '13px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0' }}>
            <span>{t('total')}:</span>
            <span>{formatNumber(totalBeforeDiscount, i18n.language)}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0' }}>
            <span>{t('discount')}:</span>
            <span>{formatNumber(discountAmount, i18n.language)}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0' }}>
            <span>{t('Total After Discount')}:</span>
            <span>{formatNumber(totalAfterDiscount, i18n.language)}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0' }}>
            <span>{t('vat', { percentage: 7 })}:</span>
            <span>{formatNumber(vatAmount, i18n.language)}</span>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            margin: '6px 0',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            <span>{t('grandTotal')}:</span>
            <span>{formatNumber(grandTotal, i18n.language)}</span>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            margin: '6px 0',
            color: 'green',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            <span>{t('Paid Amount')}:</span>
            <span>{formatNumber(payAmount, i18n.language)}</span>
          </div>
          
          {/* Only show remaining balance if needed */}
          {remainingBalance > 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              margin: '6px 0',
              color: 'red',
              fontWeight: 'bold',
              fontSize: '14px'
            }}>
              <span>{t('Remaining Balance')}:</span>
              <span>({formatNumber(remainingBalance, i18n.language)})</span>
            </div>
          )}
        </div>

        {/* Payment Information */}
        <div className="payment-info" style={{ marginTop: '40px' }}>
          <div className="payment-details" style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            marginBottom: '15px'
          }}>
            <div style={{ width: '48%' }}>
              <div style={{ 
                border: '1px solid #ddd', 
                padding: '10px', 
                borderRadius: '4px',
                backgroundColor: '#f9f9f9'
              }}>
                <h4 style={{ 
                  fontSize: '13px', 
                  fontWeight: 'bold', 
                  marginBottom: '8px',
                  color: '#000000'
                }}>{t('Payment Method')}</h4>
                <p style={{ fontSize: '12px', margin: '0', color: '#000000' }}>
                  {paymentData?.payment_method || t('na')}
                </p>
                {paymentData?.memo && (
                  <p style={{ fontSize: '12px', marginTop: '5px', color: '#000000' }}>
                    <strong>{t('Memo')}:</strong> {paymentData.memo}
                  </p>
                )}
                <p style={{ 
                  fontSize: '12px', 
                  marginTop: '8px', 
                  fontWeight: 'bold', 
                  color: 'green' 
                }}>
                  {t('Payment Status')}: {t('Confirmed')}
                </p>
              </div>
            </div>
            
            <div style={{ width: '48%' }}>
              <div style={{ 
                border: '1px solid #ddd', 
                padding: '10px', 
                borderRadius: '4px',
                backgroundColor: '#f9f9f9'
              }}>
                <h4 style={{ 
                  fontSize: '13px', 
                  fontWeight: 'bold', 
                  marginBottom: '8px',
                  color: '#000000'
                }}>{t('Course Information')}</h4>
                <p style={{ fontSize: '12px', margin: '0', color: '#000000' }}>
                  <strong>{t('Course')}:</strong> {paymentData?.class_code || t('na')}
                </p>
                <p style={{ fontSize: '12px', marginTop: '5px', color: '#000000' }}>
                  <strong>{t('Hours')}:</strong> {paymentData?.total_hours} {t('hrs')}
                </p>
                <p style={{ fontSize: '12px', marginTop: '5px', color: '#000000' }}>
                  <strong>{t('Amount Paid')}:</strong> {formatNumber(payAmount, i18n.language)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="signature-section" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginTop: '100px',
          marginBottom: '10px',
          padding: '0 50px',
          fontSize: '9px'
        }}>
          {paymentData?.signature ? (
            <div className="signature-box" style={{ textAlign: 'center', width: '100%' }}>
              <img 
                src={paymentData.signature} 
                alt="Signature" 
                style={{ maxWidth: '200px', maxHeight: '60px', marginBottom: '5px' }} 
              />
              <p style={{ color: '#000000' }}>{t('signature')}</p>
            </div>
          ) : (
            <>
              <div className="signature-box" style={{ textAlign: 'center', width: '200px' }}>
                <div className="signature-line" style={{ 
                  borderTop: '1px solid #000', 
                  marginBottom: '5px',
                  width: '100%',
                  color: '#000000'
                }}></div>
                <p style={{ color: '#000000' }}>{t('Authorized Signature')}</p>
              </div>
              
              <div className="signature-box" style={{ textAlign: 'center', width: '200px' }}>
                <div className="signature-line" style={{ 
                  borderTop: '1px solid #000', 
                  marginBottom: '5px',
                  width: '100%',
                  color: '#000000'
                }}></div>
                <p style={{ color: '#000000' }}>{t('companyStamp')}</p>
              </div>
            </>
          )}
        </div>

        {/* Note */}
        <div className="invoice-note" style={{ 
          textAlign: 'center', 
          marginTop: '20px',
          marginBottom: '15px'
        }}>
          <p style={{ fontSize: '9px', fontStyle: 'italic' }}>
            บริษัทขอสงวนสิทธิ์ในการคืนเงินทุกกรณี / No refunds under any circumstances.
          </p>
        </div>

        {/* Print Button */}
        <div className="invoice-actions" 
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '15px',
            } as any}>
          <button 
            onClick={handlePrint}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4646ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {t('printReceipt')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentReceiptModal;